<template lang="html">
<div>
  <toolbar title="Load Board">
    <v-icon @click="filterResults">refresh</v-icon>
  </toolbar>
  <v-layout column>
    <v-layout row justify-center>
      <v-dialog
        v-model="showTruckingInfoPrompt"
        max-width="400">
        <v-card>
          <v-card-title class="headline">
            Before placing bids, please add trucking information to your company profile.
          </v-card-title>
          <div v-if="companies" v-for="companyObject in companies" :key="companyObject.id">
              <v-btn
                v-if="!_.get(companyObject, 'producerInfo.billingAddress.city', false)"
                align-center
                color="gray"
                @click="$router.push({ path: `/companies/${companyObject.id}/profile`})">
                    <v-icon left>business</v-icon>
                    {{ companyObject.companyName }} Company Profile
              </v-btn>
          </div>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              flat="flat"
              @click="showTruckingInfoPrompt = false">
              Close
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-layout>

    <v-layout row v-if="searchSuggestions.length">
      <v-flex>
        <v-alert :value="true" color="primary" type="info">
          Sorry, no results were found with those search criteria.
          Maybe you will find one of these listings interesting...
        </v-alert>
      </v-flex>
    </v-layout>
    <v-layout row v-else-if="!filteredResults.length && isMobile && !loadingResults">
      <v-flex>
        <v-alert :value="true" color="primary" type="info">
          No results at this time.
          Be sure to subscribe to the
          <router-link v-if="signedIn" class="link ma-0 pa-0" to="notification-settings">
            Loadboard notifications
          </router-link>
          <template v-else>Loadboard notifications</template> to hear when loads become available.
          Be sure to select all of your interested states / regions.
        </v-alert>
      </v-flex>
    </v-layout>

    <v-card class="mt-1">
      <v-layout row wrap>
        <v-flex xs12 md5 lg2 class="mx-2">
          <v-menu
            ref="dateMenu1"
            :close-on-content-click="false"
            v-model="dateMenu1"
            :nudge-right="40"
            :return-value.sync="sincePickupDateTime"
            lazy
            transition="scale-transition"
            offset-y
            full-width
            min-width="290px">
            <v-text-field
              slot="activator"
              v-model="sincePickupDateTime"
              label="Leaving on or After"
              prepend-icon="event"
              :append-icon="sincePickupDateTime?'close':''"
              :append-icon-cb="()=>{sincePickupDateTime = null}"
              readonly></v-text-field>
            <v-date-picker v-model="sincePickupDateTime" no-title scrollable>
              <v-spacer></v-spacer>
              <v-btn flat color="primary" @click="dateMenu1 = false">Cancel</v-btn>
              <v-btn flat color="primary"
                     @click="$refs.dateMenu1.save(sincePickupDateTime)">OK
              </v-btn>
            </v-date-picker>
          </v-menu>
        </v-flex>
        <v-flex xs12 md5 lg2 class="mx-2">
          <v-menu
            ref="dateMenu2"
            :close-on-content-click="false"
            v-model="dateMenu2"
            :nudge-right="40"
            :return-value.sync="untilPickupDateTime"
            lazy
            transition="scale-transition"
            offset-y
            full-width
            min-width="290px"
          >
            <v-text-field
              slot="activator"
              v-model="untilPickupDateTime"
              label="Leaving on or Before"
              prepend-icon="event"
              :append-icon="untilPickupDateTime?'close':''"
              :append-icon-cb="()=>{untilPickupDateTime = null}"
              readonly
            ></v-text-field>
            <v-date-picker v-model="untilPickupDateTime" no-title scrollable>
              <v-spacer></v-spacer>
              <v-btn flat color="primary" @click="dateMenu2 = false">Cancel</v-btn>
              <v-btn flat color="primary" @click="$refs.dateMenu2.save(untilPickupDateTime)">
                OK
              </v-btn>
            </v-date-picker>
          </v-menu>
        </v-flex>
        <v-flex xs12 md5 lg3 class="mx-2">
          <address-search label="Leaving From"
                          @changeLocation="locationSearchChanged"
                          currentLocationBtn></address-search>
        </v-flex>
        <v-flex xs12 md5 lg2 class="mx-2">
          <v-text-field v-model="radius" clearable :label="radiusLabel"></v-text-field>
        </v-flex>
        <v-flex xs12 md5 lg1 class="mx-3">
          <v-btn class="outline clearable accent" @click="search">
            <v-icon color="white">search</v-icon>
            Search
          </v-btn>
        </v-flex>
      </v-layout>
    </v-card>

    <v-layout v-if="isMobile" sm12 class="mt-2" column>
      <v-card v-for="load in filteredResults" :key="load.id" class="xs12 my-2">
         <v-tabs>
          <v-tab ripple> Details </v-tab>
          <v-tab ripple> Bid </v-tab>
          <v-tab-item :key="1">
          <v-card-text>
            <v-layout column class="mb-2">
              <h2 v-if="signedIn">
                <CompanyName :company="load.listingCompany" />
              </h2>
              <h4 v-else class="pb-2">
                <span class="font-italic">Register now to see load {{$t('booked-load.producers', 2).toLocaleLowerCase()}}</span>
              </h4>

              <span>
                {{ getVisibleCount(load) | pluralize('load', { includeNumber: true }) }} still available.
              </span>
              <span>
                {{ load.bids.placed | pluralize('bid', { includeNumber: true }) }} placed.
                <span v-if="load.bids.placed > 0">(Lowest bid: ${{load.bids.minimumBid}})</span>
              </span>
            </v-layout>
            <v-layout column>
                <strong>From: </strong>
                <location-label :listing="load.from"></location-label>
                <strong>To: </strong>
                <location-label :listing="load.to"></location-label>
                <p class="subheading"><span class="font-italic font-weight-light">
                {{ distanceLabel(load.trip.loadedMiles) }}
                </span></p>

            </v-layout>
            <v-layout column class="mb-2"  v-if="load.requirements.dispatchInstructions">
              <h4>Instructions:</h4>
              <span>
                {{load.requirements.dispatchInstructions}}
              </span>
            </v-layout>

            <v-layout row>
             <icon-base v-if="load.requirements.requireWashout"
                         width="25" height="25" icon-color="#352208"
                         icon-name="hose" tooltip="Washout Required"><icon-hose /></icon-base>
              <icon-base v-if="load.requirements.requireWeatherboards"
                         width="25" height="25" icon-color="#352208"
                         icon-name="cold" tooltip="Weatherboards Required">
                <icon-cold />
              </icon-base>
              <icon-base v-if="load.requirements.requireWoodChips"
                         width="25" viewBox="0 0 5 5"
                         height="25" icon-name="wood" icon-color="#352208" tooltip="Wood Chips Required">
                <icon-wood />
              </icon-base>
            </v-layout>
            <b>Pickup Date:</b> {{load.from.pickupWindow.pickupAt | formatDate}}<br/>
            <load-map
              hideOnSmall
              disable-zoom
              :origin-place-id="_.get(load, 'from.googlePlaceId', null)"
              :destination-place-id="_.get(load, 'to.googlePlaceId', null)" />

          </v-card-text>
      </v-tab-item>
      <v-tab-item :key="2">
        <bid-form
          hideMapAndConversation
          @refresh="filterResults"
          :listing="load" />

      </v-tab-item>
      </v-tabs>
      </v-card>
    </v-layout>
    <v-layout v-else sm12 class="mt-2" column>
      <v-data-table
        ref="resultsTable"
        :headers="headers"
        :items="filteredResults"
        expand
        :loading="loadingResults"
        item-key="listingHumanId"
        class="elevation-1"
        :rows-per-page-items="[10,20,30]">
        <template v-if="!loadingResults"  slot="no-data">
          <v-alert :value="true" color="primary" type="info">
            No results at this time.
            Be sure to subscribe to the
            <router-link v-if="signedIn" class="link ma-0 pa-0" to="notification-settings">
              Loadboard notifications
            </router-link>
            <template v-else>Loadboard notifications</template>
            to hear when loads become available.
            Be sure to select all of your interested states / regions.
          </v-alert>
        </template>
        <template slot="headerCell" slot-scope="props">
          <v-tooltip bottom>
            <span slot="activator">
              {{ props.header.text }}
            </span>
            <span>
              {{ props.header.text }}
            </span>
          </v-tooltip>
        </template>
        <template slot="items" slot-scope="props">
          <tr @click="checkTruckingInfoBeforeDecidingToExpandRow(props.item, props.expanded) ?
          props.expanded = !props.expanded : null"
          :class="{'cursor-pointer': shouldShowRowExpandSlot(props.item)}" >
            <td class="id-col">
              <template v-if="shouldShowRowExpandSlot(props.item)">
                <v-icon class="expand-icon" v-show="!props.expanded">expand_more</v-icon>
                <v-icon class="expand-icon" v-show="props.expanded">expand_less</v-icon>
              </template>


              <v-chip v-if="hasPlacedBid(props.item) && hasAcceptedBid(props.item)"
                      color="green" text-color="white">
                Accepted
                <v-icon right>check_circle</v-icon>
              </v-chip>
              <template v-else-if="hasPlacedBid(props.item)">
                <v-btn color="accent">
                  <v-icon size="18" color="white">edit</v-icon>
                  <span class="pl-2">Modify Bid</span>
                </v-btn>
              </template>
              <template v-else-if="isListingOwner(props.item)">
                <v-btn color="accent">
                <v-icon size="18" color="white">visibility</v-icon>
                <span class="pl-2" @click="viewListingDetail(props.item.listingHumanId)">
                  Modify Listing
                </span>
                </v-btn>
              </template>
              <template v-else>
                <v-btn color="accent">
                  <v-icon size="18" color="white">visibility</v-icon>
                  <span class="pl-2" v-if="isLoadboardBiddingEnabled">Place Bid</span>
                  <span class="pl-2" v-else>Submit Availability</span>
                </v-btn>
              </template>
            </td>
            <td>
              <span v-if="signedIn"><CompanyName :company="props.item.listingCompany" /></span>
              <span v-else>Log in to view company.</span>
            </td>
            <td class="text-xs-left">
              <v-flex>
                <b>From:</b>
                <location-label :listing="props.item.from"></location-label>
              </v-flex>
              <v-flex>
                <b>To:</b>
                <location-label :listing="props.item.to"></location-label>
              </v-flex>
            </td>
            <td class="text-xs-left">
              {{props.item.from.pickupWindow.pickupAt | formatDate}}
            </td>
            <td class="text-xs-left">
              {{ distanceLabel(props.item.trip.loadedMiles) }}
              <br />
              <icon-base v-if="props.item.requirements.requireWashout"
                         width="25" height="25" icon-color="#352208"
                         icon-name="hose" tooltip="Washout Required"><icon-hose /></icon-base>
              <icon-base v-if="props.item.requirements.requireWeatherboards"
                         width="25" height="25" icon-color="#352208"
                         icon-name="cold" tooltip="Weatherboards Required">
                <icon-cold />
              </icon-base>
              <icon-base v-if="props.item.requirements.requireWoodChips"
                         width="25" viewBox="0 0 5 5"
                         height="25" icon-name="wood" icon-color="#352208" tooltip="Wood Chips Required">
                <icon-wood />
              </icon-base>
            </td>
            <td class="text-xs-left">
                <span>
                  {{ getVisibleCount(props.item) | pluralize('load', { includeNumber: true })}} still available.
                </span><br/>
                <span>
                  {{ props.item.bids.placed | pluralize('bid', { includeNumber: true }) }} placed.
                  <span v-if="props.item.bids.placed > 0">(Lowest bid: ${{props.item.bids.minimumBid}})</span>
                </span>
            </td>
          </tr>
        </template>
        <template slot="expand" slot-scope="slotItems"
                  v-if="shouldShowRowExpandSlot(slotItems.item)">
          <v-card pl-4>
            <v-card-text class="pt-1"  v-if="slotItems.item.requirements.dispatchInstructions">
              <h4>Instructions:&nbsp;</h4>
              <span>
                {{slotItems.item.requirements.dispatchInstructions}}
              </span>
            </v-card-text>

            <bid-form @refresh="filterResults"
                      :listing="slotItems.item">
            </bid-form>
          </v-card>
        </template>
      </v-data-table>
    </v-layout>
  </v-layout>
  </div>
</template>

<script>
import _ from 'lodash';
import Vue2Filters from 'vue2-filters';
import API from '@/api';
import firebase from 'firebase';
import companyNamesMixin from '@/mixins/companyNames';
import Toolbar from '@/components/Toolbar';
import AddressSearch from '@/components/AddressSearch';
import VFloatField from '@/components/VFloatField';
import BidForm from '@/components/BidForm';
import IconBase from '@/components/IconBase';
import CompanyName from '@/components/CompanyName';
import IconHose from '@/components/icons/IconHose';
import IconWood from '@/components/icons/IconWood';
import IconCold from '@/components/icons/IconCold';
import LoadMap from '@/components/LoadMap';
import * as Listings from '@/types/models/listing';
import * as types from '@/store/mutationTypes';
import { mapGetters } from 'vuex';
import { bidStatus } from '@/types';
import LocationLabel from '../components/LocationLabel';
import pluginMixin from '@/mixins/pluginMixin';
import unitsOfMeasurePlugin from '../mixins/unitsOfMeasurePlugin';

export default {
  mixins: [
    companyNamesMixin,
    Vue2Filters.mixin,
    pluginMixin,
    unitsOfMeasurePlugin,
  ],
  props: {
    searchListingId: {
      type: String,
    },
  },
  data: () => ({
    dateMenu1: false,
    dateMenu2: false,
    sincePickupDateTime: null,
    untilPickupDateTime: null,
    loadingResults: false,
    fromAddress: '',
    radius: null,
    lat: null,
    lng: null,
    searchResults: [],
    searchSuggestions: [],
    sortBy: 'Pickup Date',
    headers: [
      { text: ' ', sortable: false, class: 'id-col-header' },
      { text: 'Company', sortable: false },
      { text: 'Load Route', sortable: false },
      { text: 'Pickup Date', sortable: false },
      { text: 'Loaded Miles', sortable: false },
      { text: 'Availability', sortable: false },
    ],
    showTruckingInfoPrompt: false,
  }),
  components: {
    LocationLabel,
    LoadMap,
    Toolbar,
    AddressSearch,
    IconBase,
    IconHose,
    IconWood,
    IconCold,
    VFloatField,
    BidForm,
    CompanyName,
  },
  computed: {
    _() {
      return _;
    },
    ...mapGetters('companies', {
      companies: 'companies',
    }),
    ...mapGetters('applications', [
      'signedIn',
    ]),
    filteredResults() {
      let filtered = this.searchResults;

      if (!filtered.length) {
        filtered = this.searchSuggestions;
      }
      return filtered;
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    radiusLabel() {
      return `Radius (${ this.siteDistanceMeasureUnitsLabel })`;
    }
  },
  watch: {
    filteredResults() {
      if (this.searchListingId) {
        this.expandRows();
      }
    },
  },
  methods: {
    ...mapGetters('profiles', [
      'findCompanyAccount',
    ]),
    getVisibleCount(load) {
      const available = load.bids.availableLoadCount;
      return _.min([load.visibleCount, available]);
    },
    viewListingDetail(listingId) {
      this.$router.push({ name: 'listing.detail', params: { id: listingId } });
    },
    search() {
      this.filterResults();
      window.__insp.push(['tagSession', 'search_loadboard']);
    },
    filterResults() {
      this.$router.replace({ listingId: null });
      this.loadResults(this.getFilters());
    },
    expandRows() {
      for (let i = 0; i < this.filteredResults.length; i += 1) {
        const item = this.filteredResults[i];
        this.$set(this.$refs.resultsTable.expanded, item.listingHumanId, true);
      }
    },
    async loadResults(filters) {
      this.$store.commit(`applications/${types.START_LOADING}`);
      this.loadingResults = true;
      try {
        const apiResponse = await API.loadboard.search(filters || {});
        this.searchResults = _.filter(apiResponse.data.results, load => !!load.bids);
        this.searchSuggestions = [];
        this.$store.dispatch('companies/getCompanies');
      } catch (errorResponse) {
        if (errorResponse.message) {
          this.$store.dispatch('applications/setErrorNotification', {
            message: errorResponse.message,
          });
        } else {
          this.searchResults = [];
          this.searchSuggestions = errorResponse.results;
        }
      } finally {
        this.loadingResults = false;
        this.$store.commit(`applications/${types.END_LOADING}`);
      }
    },
    locationSearchChanged(val) {
      this.lat = val ? val.lat : null;
      this.lng = val ? val.lng : null;
    },
    getFilters() {
      const filters = {};
      if (this.sincePickupDateTime) {
        filters.from = this.sincePickupDateTime;
      }

      if (this.untilPickupDateTime) {
        filters.until = this.untilPickupDateTime;
      }

      if (this.radius && this.lat && this.lng) {
        const adjustedRadius = this.normalizeDistanceUnitOfMeasureToMiles(this.radius);

        filters.radiusInMiles = adjustedRadius;
        filters.focusLat = this.lat;
        filters.focusLng = this.lng;
      }

      return filters;
    },
    isListingOwner(listing) {
      return !_.isEmpty(this.findCompanyAccount()(listing.listingCompany.companyId));
    },
    checkTruckingInfoBeforeDecidingToExpandRow(listing, expanded) {
      for (var i = 0; i < this.companies.length; i++) {
        try {
          if (this.companies[i].truckingInfo.dotNumber) {
            console.log('doesnt exist');
          }
        } catch (err) {
          if (!expanded) {
            this.showTruckingInfoPrompt = true;
            return !this.isListingOwner(listing);
          }
        }
      }
      return !this.isListingOwner(listing);
    },
    shouldShowRowExpandSlot(listing) {
      // have I already placed a bid? am I a member of the owning organization?
      // No need to show inline bid.
      return !this.isListingOwner(listing);
    },
    placedBids(listing) {
      return Listings.findPlacedBids(listing, this.userProfile);
    },
    hasPlacedBid(listing) {
      const bids = this.placedBids(listing);
      return bids && bids.length > 0;
    },
    hasAcceptedBid(listing) {
      const bids = this.placedBids(listing);
      return bids[0].status === bidStatus.ACCEPTED;
    },
    loadWithUrlFilters() {
      this.loadResults(this.searchListingId ? { listingId: this.searchListingId } : null);
    },
  },
  destroyed() {
    this.$bus.$off('authStateChanged', this.loadWithUrlFilters);
  },
  created() {
    if (this.isLoadboardEnabled) {
      this.$bus.$on('authStateChanged', this.loadWithUrlFilters);
      if (firebase.auth().currentUser) {
        this.loadWithUrlFilters();
      }
    } else {
      this.$router.replace({ name: 'landing'})
    }
  },
};
</script>

<style>
  .link {
    color: white;
  }

  .bid-date-picker {
    padding-bottom: 5px;
    margin-top: 19.5px;
  }

  .id-col-header {
    padding-left: 35px !important;
  }

  .id-col {
    position: relative;
    padding-left: 35px !important;
  }

  .expand-icon {
    position: absolute;
    left: 5px;
    top: auto;
    bottom: auto;
    margin-top: 10px;
  }

  .block-form-container {
    position: relative;
  }

  .block-form-container .block-form-overlay {
    position: absolute;
    background-color: rgba(127, 127, 127, .7);
    top: 0;
    width: calc(100% - 10px);
    height: calc(100% - 10px);
    left: 10px;
  }

</style>



// WEBPACK FOOTER //
// src/pages/LoadBoards.vue